// @flow
// A record's text is HTML-ish: the app saves its newlines as <br>, the migrations wrote <b> and whole HTML pages
// (records.pure_html), the External API stores plain text with \n. It is written by other users, so it must never
// be put in the page as it is: `<img src=x onerror=…>` in a record would run for everyone who opens the timeline.
//
// The text is parsed in a document with no browsing context (made once), in a body out of its tree (see parse):
// nothing in it runs, no image loads, and nothing in it is connected to a document. NEW nodes are made in the page's
// document from what the parser made, and only those reach the page. They go in with DOM calls, never as HTML, so
// nothing parses them again:
// - text is copied as text; comments go;
// - formatting tags (ALLOWED_TAGS) are made again with no attributes, except the numbers of a list or a table cell
//   as the browser reads them, a list's type (1 a A i I) and an open <details> (copyAttributes): no on* handlers,
//   href, style or class;
// - other blocks (center, section, form…) become a div, or the ul, pre or s they look like (RENAMED_TAGS), so their
//   text keeps its own lines;
// - what the page wouldn't show goes with its content: code and other documents (DROPPED_TAGS), what the record
//   itself hides (isHidden, invisible), and an SVG's or MathML's code, tooltip and notes (FOREIGN_DROPPED). An
//   invisible list item, cell or row, or an invisible element with something visible inside, is still made, without
//   its own text (KEEPS_BOX);
// - a form control or an image becomes the text it shows (shownAs): a dropdown its chosen option, a list box each
//   option with ☑ or ☐, a checkbox or a radio ☑ or ☐ (lastCheckedRadios), an image "[imagem: its alt]" (one the
//   record holds itself too: a data: URL can take far more memory to decode than it takes in the record). A web link
//   shows its text, then its URL (endLink);
// - any other tag (a, font, button, Word's o:p, svg, math, ruby, bdo…) is unwrapped: the tag goes, its content stays
//   (a <bdo>'s text then reads in the page's direction, a ruby's reading follows its base). SVG and MathML keep the
//   text they show (an SVG <text>, MathML's mi, mn, mo, mtext, ms), not the line breaks between their tags.
//
// The tags are close to Rails' safe list, which the API's PDF uses through simple_format, but not the same: the PDF
// keeps links (a href), images (img src), class and title, and strips u, s and tables (keeping their text). Here a
// link shows as its text and its URL, and an image as its alt.
import { globalTOrPtBr } from './basic';

const INLINE_TAGS = [
  'b', 'strong', 'i', 'em', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup', 'small', 'big', 'span', 'q',
  'code', 'kbd', 'samp', 'tt', 'var', 'abbr', 'acronym', 'cite', 'dfn', 'time',
];
const INLINE = new Set(INLINE_TAGS);
const ALLOWED_TAGS = new Set([
  ...INLINE_TAGS, 'br',
  // blocks
  'p', 'div', 'blockquote', 'pre', 'hr', 'address', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd', 'details', 'summary',
  'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]);

const BLOCKS = [
  'article', 'aside', 'center', 'dialog', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'header', 'hgroup',
  'legend', 'main', 'nav', 'search', 'section',
];
const RENAMED_TAGS: Map<string, string> = new Map([
  ...BLOCKS.map((tag): [string, string] => [tag, 'div']),
  ['dir', 'ul'], ['menu', 'ul'],
  ['listing', 'pre'], ['plaintext', 'pre'], ['xmp', 'pre'],
  ['strike', 's'],
]);

// Gone together with their content: code, other documents, and what a page never shows (a datalist's suggestions,
// the fallback text of a video or a canvas, the parentheses of a ruby for browsers without ruby)
const DROPPED_TAGS = new Set([
  'script', 'style', 'template', 'noscript', 'noembed', 'noframes', 'iframe', 'title',
  'datalist', 'audio', 'video', 'canvas', 'rp',
]);

// SVG and MathML: the elements whose text shows (and an SVG's foreignObject, which holds HTML), and what never shows
const FOREIGN_TEXT = new Set(['text', 'tspan', 'textPath', 'foreignObject', 'mi', 'mn', 'mo', 'mtext', 'ms']);
const FOREIGN_DROPPED = new Set([
  'script', 'style', 'title', 'desc', 'metadata', 'defs', 'symbol', 'annotation', 'annotation-xml',
]);
// MathML's scripts, as the page's own: the base, then each script in a <sub> or a <sup>
const MATH_SCRIPTS: Map<string, Array<string>> = new Map([['msub', ['sub']], ['msup', ['sup']], ['msubsup', ['sub', 'sup']]]);

const HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

// An inline style as the browser reads it: the parsed element's own CSSOM (quotes, comments, case and !important
// as CSS has them), never a regex over the CSS text
const styleOf = (node: any, property: string): string => (
  node.style && node.hasAttribute('style') ? String(node.style[property]).toLowerCase() : ''
);

// What the record hides, with its content: hidden, an inline display:none, a dialog that isn't open
const isHidden = (node: any): boolean => {
  const html = node.namespaceURI === HTML_NAMESPACE;
  if (html && (node.hasAttribute('hidden') || (node.localName === 'dialog' && !node.hasAttribute('open')))) return true;
  return styleOf(node, 'display') === 'none';
};

// visibility is inherited: an element is invisible when it, or the nearest element above it that sets visibility,
// says hidden or collapse (visibilityOf: which one, '' when it shows). Something inside can set it back to visible
// (setsVisible), so an invisible element goes, with all it holds, only when nothing inside it does; otherwise it is
// made again like any other (its box keeps the lines, the numbers and the bold of what shows in it) and only its own
// text goes.
const setsVisible = (node: any): boolean => ['visible', 'initial'].includes(styleOf(node, 'visibility'));
const visibilityOf = (node: any, inherited: string): string => {
  const value = styleOf(node, 'visibility');
  if (value === 'hidden' || value === 'collapse') return value;
  return setsVisible(node) ? '' : inherited;
};
// An invisible list item, cell or row keeps its box even with nothing visible inside, as in the browser: it is made
// again, empty but with its numbers, so the next items' numbers and the table's columns don't shift. A collapsed row
// is the exception: the browser removes it (0 high), with what shows inside it, and so every row of a collapsed
// tbody, thead or tfoot, even one that sets visible.
const KEEPS_BOX = new Set(['li', 'td', 'th', 'tr']);
const ROW_GROUPS = new Set(['tbody', 'thead', 'tfoot']);
// (invisible: the row's visibility; inherited: its parent's, a row group's)
const isCollapsedRow = (node: any, invisible: string, inherited: string): boolean => (
  node.localName === 'tr'
  && (invisible === 'collapse' || (inherited === 'collapse' && ROW_GROUPS.has(node.parentNode.localName)))
);
// The elements with something inside that sets visibility back, which keep their box too: each ancestor of such an
// element, marked once
const withVisibleInside = (root: any): Set<any> => {
  const marked = new Set();
  const all = root.getElementsByTagName('*');
  for (let i = 0; i < all.length; i += 1) {
    if (setsVisible(all[i])) {
      for (let up = all[i].parentNode; up && up !== root && !marked.has(up); up = up.parentNode) marked.add(up);
    }
  }
  return marked;
};

const LIST_TYPES = new Set(['1', 'a', 'A', 'i', 'I']);
// A number as the browser reads start="4x" or value=" 2" (4, 2): none from "abc", and none past a 32-bit integer,
// which the browser ignores too (its default numbering applies, so nothing is written)
const INT32 = 2 ** 31;
const readNumber = (value: ?string): ?number => {
  const match = /^[\t\n\f\r ]*([-+]?\d+)/.exec(value || '');
  const number = match ? Number(match[1]) : NaN;
  return number >= -INT32 && number < INT32 ? number : null;
};

// What goes from the record's element to the new one: the numbers as the browser reads them (a cell's properties
// also clamp a colspan to 1–1000, keep rowspan 0…), written back as plain numbers, a list's type and details' open
const copyAttributes = (from: any, to: any) => {
  switch (from.localName) {
    case 'ol': {
      const start = readNumber(from.getAttribute('start'));
      if (start !== null && start !== undefined) to.setAttribute('start', String(start));
      if (from.reversed) to.setAttribute('reversed', '');
      if (LIST_TYPES.has(from.getAttribute('type'))) to.setAttribute('type', from.getAttribute('type'));
      break;
    }
    case 'li': {
      const value = readNumber(from.getAttribute('value'));
      if (value !== null && value !== undefined) to.setAttribute('value', String(value));
      break;
    }
    case 'td':
    case 'th':
      if (from.colSpan !== 1) to.colSpan = Number(from.colSpan);
      if (from.rowSpan !== 1) to.rowSpan = Number(from.rowSpan);
      break;
    case 'details':
      if (from.open) to.setAttribute('open', '');
      break;
    default:
  }
};

// Radios with one name are one group, and the page's parser leaves only the last checked one of a group checked. Out
// of a tree the browser groups them only within a form (Chrome, jsdom), so it is done here: per form around it (the
// closest one, or none) and name, as written, the last checked radio is the one that stays checked
const lastCheckedRadios = (root: any): Set<any> => {
  const groups: Map<any, Map<string, any>> = new Map();
  const inputs = root.getElementsByTagName('input');
  for (let i = 0; i < inputs.length; i += 1) {
    const input = inputs[i];
    if (input.namespaceURI === HTML_NAMESPACE && input.type === 'radio' && input.checked && input.name) {
      const form = input.closest('form');
      const byName = groups.get(form) || new Map();
      groups.set(form, byName.set(input.name, input));
    }
  }
  const last = new Set();
  groups.forEach((byName) => byName.forEach((input) => last.add(input)));
  return last;
};

// The text a form control or an image shows in its place ('' when it is invisible), or null for any other element.
// isChecked: whether a checkbox or a radio shows checked.
const SHOWS_VALUE = new Set([
  'text', 'search', 'tel', 'url', 'email', 'number', 'date', 'time', 'datetime-local', 'month', 'week',
  'button', 'submit', 'reset',
]);
const image = (alt: string): string => {
  // The app's language, without React: the Portuguese text until i18n is set up
  const word = globalTOrPtBr('label/record_image');
  return alt.trim() ? `[${word}: ${alt.trim()}]` : `[${word}]`;
};
const checkMark = (checked: boolean): string => (checked ? '☑' : '☐');
// As the browser shows an option: its label when it has one that isn't empty, otherwise its text
const optionLabel = (option: any): string => option.getAttribute('label') || option.text;
// A list box shows its options, each ☑ when chosen, ☐ when not: not the ones the record hides or makes invisible,
// by themselves or in their optgroup (what lies between the list box and the option), and those set visible in an
// invisible list box too. To keep it simple an optgroup's heading isn't shown, only its options.
const optionShows = (option: any, select: any, invisible: string): boolean => {
  const path = [];
  for (let el = option; el && el !== select; el = el.parentNode) path.unshift(el);
  if (path.some(isHidden)) return false;
  return !path.reduce((visibility, el) => visibilityOf(el, visibility), invisible);
};
const listBoxText = (select: any, invisible: string): string => (
  Array.prototype.filter.call(select.options, (option) => optionShows(option, select, invisible))
    .map((option) => `${checkMark(option.selected)} ${optionLabel(option)}`).join(' ')
);
const controlText = (node: any, isChecked: (input: any) => boolean): ?string => {
  switch (node.localName) {
    case 'select':
      // A dropdown shows the option the browser picked (the first one that isn't disabled), nothing when none is
      return node.selectedOptions.length > 0 ? optionLabel(node.selectedOptions[0]) : '';
    case 'input':
      if (node.type === 'checkbox' || node.type === 'radio') {
        // Before its label, with a space unless the label's text starts with one
        const next = node.nextSibling;
        const spaced = next && next.nodeType === TEXT_NODE && /^\s/.test(next.data);
        return `${checkMark(isChecked(node))}${spaced ? '' : ' '}`;
      }
      if (node.type === 'image') return image(node.alt);
      return SHOWS_VALUE.has(node.type) ? node.value : '';
    case 'textarea':
      return node.value;
    case 'img':
      return image(node.alt);
    default:
      return null;
  }
};
const shownAs = (node: any, invisible: string, isChecked: (input: any) => boolean): ?string => {
  if (node.localName === 'select' && (node.multiple || node.size > 1)) return listBoxText(node, invisible);
  const text = controlText(node, isChecked);
  return text && invisible ? '' : text;
};

// A web link's URL, to show after its text: http(s) only, as the browser reads it (no tab or newline, no space
// around it), its other spaces shown as one, and not when the text already says it
const linkUrl = (node: any): ?string => {
  const href = (node.getAttribute('href') || '').replace(/[\t\n\r]/g, '').trim().replace(/\s+/g, ' ');
  if (!/^https?:\/\//i.test(href)) return null;
  const bare = (text) => text.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '').toLowerCase();
  return bare(node.textContent) === bare(href) ? null : href;
};

// A document with no browsing context, made once: an element parsed in it runs no script or handler and loads no
// image, as with DOMParser, without making a new document for every record (the record editor parses in it too)
let inertDocument = null;
export const getInertDocument = (): any => {
  if (!inertDocument) inertDocument = document.implementation.createHTMLDocument('');
  return inertDocument;
};
const parse = (html: string) => {
  // A body of its own, out of that document's tree: parsed as innerHTML parses it in the app (straight in a body,
  // in standards mode: leading newlines kept, head tags such as title, style or meta where they are written). Out of
  // a tree nothing the parser makes is ever connected, so a meta, base, link or style in the record changes nothing:
  // in Chrome a <meta name="referrer"> in the inert document's own body sets the referrer policy of the page itself.
  // Radios aren't grouped there (lastCheckedRadios). Emptied once the record is made (sanitizeRecordFragment).
  const body = getInertDocument().createElement('body');
  body.innerHTML = html;
  return body;
};

// Where the walk puts what it finds: the new element; whether text shows there (not between SVG or MathML tags);
// the visibility it is in ('' when it shows, or hidden or collapse); and, in an SVG, how many of its text blocks
// showed something
type Into = { el: any, text: boolean, foreign: boolean, invisible: string, svg: ?{ texts: number } };

const build = (root: any) => {
  const fragment = document.createDocumentFragment();

  // Depth-first with a stack, not recursion: a record nested thousands of tags deep can't overflow the call stack.
  // A function on it runs once what was pushed after it is done (the end of a link, an SVG, a text block…), a string
  // is text to add then (a fraction's /).
  const pending: Array<any> = [];
  const pushChildren = (from: any, into: Into) => {
    for (let i = from.childNodes.length - 1; i >= 0; i -= 1) pending.push([from.childNodes[i], into]);
  };

  let shown = 0; // the visible pieces added so far: text that isn't only spaces
  let lastChar = ''; // the last character added, '' at the start of a line
  let gap = ''; // what an SVG asks for before the next visible piece: ' ' (after it) or 'br' (between its texts)
  let lastLink = null; // the URL added after the last link: { url, node, shown }
  let visibleInside = null; // withVisibleInside, the first time an invisible element is met
  let checkedRadios = null; // lastCheckedRadios, the first time a checked radio with a name is met
  const isChecked = (input: any): boolean => {
    if (input.type !== 'radio' || !input.checked || !input.name) return input.checked;
    if (!checkedRadios) checkedRadios = lastCheckedRadios(root);
    return checkedRadios.has(input);
  };

  const startVisible = (into: Into, startsWithSpace: boolean) => {
    if (gap === 'br') {
      into.el.appendChild(document.createElement('br'));
      lastChar = '';
    } else if (gap === ' ' && lastChar && /\S/.test(lastChar) && !startsWithSpace) {
      into.el.appendChild(document.createTextNode(' '));
    }
    gap = '';
    shown += 1;
  };
  const addText = (value: ?string, into: Into) => {
    if (!value || into.invisible) return;
    // SVG and MathML show the spaces in their text as one
    let text = into.foreign ? value.replace(/[\t\n\f\r ]+/g, ' ') : value;
    if (/\S/.test(text)) {
      if (gap === 'br') text = text.replace(/^\s+/, '');
      startVisible(into, /^\s/.test(text));
    } else if (gap === 'br') {
      return; // spaces at the start of the next line
    } else if (gap === ' ') {
      gap = '';
    }
    into.el.appendChild(document.createTextNode(text));
    lastChar = text.slice(-1);
  };
  // A block or a line break starts a new line, and what follows a block too: no space for an SVG there
  const newLine = () => {
    lastChar = '';
    gap = '';
  };
  const make = (tag: string, into: Into) => {
    const el = document.createElement(tag);
    into.el.appendChild(el);
    if (!INLINE.has(tag)) {
      newLine();
      pending.push(newLine);
    }
    return el;
  };

  // A link's URL goes once, after the last piece of it that shows: when a block closes a link the parser opens it
  // again (the same href) in what follows, and when nothing visible came between two links to the same URL (only
  // spaces or the blocks the parser reopened it in), the URL already added after the first is moved after this one.
  // Not after a link that shows nothing.
  const endLink = (url: string, start: number, into: Into) => {
    if (shown === start) return;
    if (lastLink && lastLink.url === url && lastLink.shown === start) lastLink.node.parentNode.removeChild(lastLink.node);
    const node = document.createTextNode(` (${url})`);
    into.el.appendChild(node);
    lastLink = { url, node, shown };
    lastChar = ')';
    gap = '';
  };

  const visitForeign = (node: any, into: Into, invisible: string) => {
    const tag = node.localName;
    if (FOREIGN_DROPPED.has(tag)) return;
    const inside = { ...into, text: FOREIGN_TEXT.has(tag) || (into.foreign && into.text), foreign: true, invisible };
    const scripts = MATH_SCRIPTS.get(tag);
    if (invisible) {
      pushChildren(node, inside);
    } else if (tag === 'svg' && node.namespaceURI === SVG_NAMESPACE && !into.foreign) {
      // An SVG in the text: a space before and after what it shows, when the words around it don't have one
      const before = gap;
      const start = shown;
      gap = ' ';
      pending.push(() => { gap = shown > start ? ' ' : before; });
      pushChildren(node, { ...inside, svg: { texts: 0 } });
    } else if ((tag === 'text' || tag === 'foreignObject') && inside.svg) {
      // Each of its texts on a line of its own
      const { svg } = inside;
      const start = shown;
      if (svg.texts > 0) gap = 'br';
      pending.push(() => { if (shown > start) svg.texts += 1; });
      pushChildren(node, inside);
    } else if (tag === 'mfrac' && node.children.length === 2) {
      // A fraction reads 1/2, not 12
      pending.push([node.children[1], inside], ['/', inside], [node.children[0], inside]);
    } else if (scripts && node.children.length === scripts.length + 1) {
      // x² reads x<sup>2</sup>, not x2
      for (let i = scripts.length; i >= 1; i -= 1) {
        const script = node.children[i];
        const wrap = scripts[i - 1];
        pending.push(() => pending.push([script, { ...inside, el: make(wrap, inside) }]));
      }
      pending.push([node.children[0], inside]);
    } else {
      pushChildren(node, inside);
    }
  };

  const keepsBox = (node: any, isHtml: boolean): boolean => {
    if (isHtml && KEEPS_BOX.has(node.localName)) return true;
    if (!visibleInside) visibleInside = withVisibleInside(root);
    return visibleInside.has(node);
  };

  const visit = (node: any, into: Into) => {
    const invisible = visibilityOf(node, into.invisible);
    const isHtml = node.namespaceURI === HTML_NAMESPACE;
    const tag = node.localName;
    // A collapsed row goes with all it holds, and so does an invisible element, unless it keeps its box: then it is
    // made again as any other below, and only its own text goes (inside.invisible)
    if ((isHtml && isCollapsedRow(node, invisible, into.invisible)) || (invisible && !keepsBox(node, isHtml))) return;
    if (!isHtml) {
      // SVG and MathML: never made again, only their text
      visitForeign(node, into, invisible);
      return;
    }
    if (DROPPED_TAGS.has(tag)) return;
    const inside = { el: into.el, text: true, foreign: false, invisible, svg: null };
    const shownText = shownAs(node, invisible, isChecked);
    if (shownText !== null && shownText !== undefined) {
      // As visible as the control itself, not its parent: one set visible in an invisible parent shows (shownAs
      // gives nothing for an invisible one)
      addText(shownText, { ...inside, invisible: '' });
      return;
    }
    const out = ALLOWED_TAGS.has(tag) ? tag : RENAMED_TAGS.get(tag);
    let target = inside;
    if (out) {
      const el = make(out, into);
      if (out === tag) copyAttributes(node, el);
      target = { ...inside, el };
    }
    const url = tag === 'a' ? linkUrl(node) : null;
    if (url) {
      const start = shown;
      pending.push(() => endLink(url, start, target));
    }
    pushChildren(node, target);
  };

  pushChildren(root, { el: fragment, text: true, foreign: false, invisible: '', svg: null });
  while (pending.length > 0) {
    const task = pending.pop();
    if (typeof task === 'function') {
      task();
    } else {
      const [node, into] = task;
      if (typeof node === 'string') {
        addText(node, into);
      } else if (node.nodeType === TEXT_NODE) {
        if (into.text) addText(node.data, into);
      } else if (node.nodeType === ELEMENT_NODE && !isHidden(node)) {
        visit(node, into);
      }
      // Anything else is left out with all it holds: comments, hidden elements
    }
  }
  return fragment;
};

// Text the parser would keep exactly as it is: no tag, no entity, no carriage return (it makes \r\n a \n), no NUL
const isPlainText = (text: string): boolean => !/[<&\r\0]/.test(text);

// The record's text as nodes of the page's document, to be appended where it shows (HMLDOMInjector)
export const sanitizeRecordFragment = (html: ?string) => {
  const text = html === null || html === undefined ? '' : String(html);
  if (!isPlainText(text)) {
    const root = parse(text);
    try {
      return build(root);
    } finally {
      // Nothing of the record stays where it was parsed, even when making it fails
      root.textContent = '';
    }
  }
  const fragment = document.createDocumentFragment();
  if (text) fragment.appendChild(document.createTextNode(text));
  return fragment;
};
